import type { ProspectData } from './aiService';

type BusinessData = {
  name: string;
  normalizedCategory: string;
  website?: string;
  phone?: string;
  rating?: number;
  reviewCount?: number;
};

const HIGH_FIT_CATEGORIES = new Set([
  'auto_dealer',
  'legal_injury',
  'dental',
  'medical',
  'orthodontics',
  'insurance',
  'hvac',
  'plumbing',
  'roofing',
  'home_services',
  'financial',
  'senior_living',
  'furniture'
]);

const MODERATE_FIT_CATEGORIES = new Set([
  'fitness',
  'beauty',
  'education',
  'real_estate',
  'vision',
  'hearing'
]);

const LOW_FIT_CATEGORIES = new Set([
  'qsr_chain',
  'gas_station',
  'atm',
  'government',
  'nonprofit',
  'hotel_chain'
]);

const MULTI_LOCATION_HINTS = /\b(group|holdings|locations|franchise|network|partners|associates|centers|clinics)\b/i;

export const getGemmetricFitScore = (normalizedCategory: string, businessName: string): number => {
  const cat = (normalizedCategory || '').toLowerCase();
  if (HIGH_FIT_CATEGORIES.has(cat)) return 0.88;
  if (MODERATE_FIT_CATEGORIES.has(cat)) return 0.68;
  if (LOW_FIT_CATEGORIES.has(cat)) return 0.22;
  if (MULTI_LOCATION_HINTS.test(businessName || '')) return 0.78;
  return 0.52;
};

export const getWebPresenceScore = (prospect: ProspectData, business: BusinessData): number => {
  let score = 0;
  if (prospect.website && prospect.website.trim()) score += 0.35;
  if (prospect.phone && prospect.phone.trim()) score += 0.15;
  if (typeof prospect.rating === 'number' && prospect.rating >= 4.0) score += 0.2;
  if (typeof prospect.reviewCount === 'number') {
    if (prospect.reviewCount >= 100) score += 0.2;
    else if (prospect.reviewCount >= 25) score += 0.12;
    else if (prospect.reviewCount >= 5) score += 0.06;
  }
  if (business.name && business.name.length > 3) score += 0.1;
  return Math.min(1, score);
};

export const getMetadataCompletenessScore = (prospect: ProspectData): number => {
  let fields = 0;
  let filled = 0;
  const checks: Array<string | undefined> = [
    prospect.website,
    prospect.phone,
    prospect.address,
    prospect.category
  ];
  checks.forEach((value) => {
    fields += 1;
    if (value && String(value).trim()) filled += 1;
  });
  if (typeof prospect.rating === 'number') filled += 0.5;
  if (typeof prospect.reviewCount === 'number') filled += 0.5;
  fields += 1;
  return Math.min(1, filled / Math.max(1, fields));
};

export const getGemmetricDistanceScore = (distanceMiles: number): number => {
  if (distanceMiles <= 1) return 1;
  if (distanceMiles <= 3) return 0.85;
  if (distanceMiles <= 8) return 0.7;
  if (distanceMiles <= 15) return 0.55;
  return 0.4;
};

export const calculateGemmetricCandidateScore = (
  prospect: ProspectData,
  business: BusinessData,
  qualitySpread: number
): number => {
  const gemmetricFit = getGemmetricFitScore(business.normalizedCategory, business.name);
  const webPresence = getWebPresenceScore(prospect, business);
  const metadata = getMetadataCompletenessScore(prospect);
  const distance = getGemmetricDistanceScore(prospect.distance);

  const finalScore =
    0.4 * gemmetricFit +
    0.25 * webPresence +
    0.15 * metadata +
    0.1 * distance +
    0.1 * qualitySpread;

  return Math.min(100, Math.round(finalScore * 100));
};

export const generateGemmetricReasoning = (prospect: ProspectData, score: number): string => {
  const hasWebsite = !!(prospect.website && prospect.website.trim());
  const category = prospect.category || 'this category';

  if (score >= 80) {
    return `${prospect.name} looks like a strong Gemmetric candidate in ${category}. The business appears mature enough to care about discoverability, and ${hasWebsite ? 'its website makes AI visibility diagnostics actionable' : 'adding a website diagnostic path could still reveal major AI retrieval gaps'}.`;
  }
  if (score >= 60) {
    return `${prospect.name} may benefit from AI Visibility Infrastructure review. ${hasWebsite ? 'A Lite Scan could reveal crawlability, schema, and entity clarity issues affecting AI-generated answers.' : 'Limited public web presence may itself reduce AI discoverability.'}`;
  }
  if (score >= 40) {
    return `${prospect.name} shows mixed signals for Gemmetric. Worth a Lite Scan if the category depends on trust, local discovery, or brand authority in AI answer engines.`;
  }
  return `${prospect.name} is a lower-priority Gemmetric candidate based on category and available metadata. Run a Lite Scan only if strategic interest is high.`;
};

export const generateGemmetricRecommendations = (prospect: ProspectData, score: number): string[] => {
  if (score < 30) {
    return ['Lower-priority Gemmetric candidate', 'Consider only if strategic account value is high'];
  }

  const recs: string[] = [];
  if (prospect.website) {
    recs.push('Run Lite Scan for crawlability and schema signals');
    recs.push('Check entity clarity in title, metadata, and structured data');
  } else {
    recs.push('No website detected — major AI discoverability risk');
    recs.push('Assess whether brand entity exists outside owned web properties');
  }
  recs.push('Evaluate AI answer visibility vs local competitors');
  if ((prospect.reviewCount || 0) >= 50) {
    recs.push('Established local brand may be invisible in AI retrieval despite reputation');
  }
  return recs.slice(0, 4);
};
